import { Link } from 'react-router-dom';
import { ArrowLeft, Sparkles, Wrench, Bug, Rocket } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

type EntryType = 'feature' | 'improvement' | 'fix' | 'launch';

const ICONS: Record<EntryType, { icon: typeof Sparkles; label: string; tone: string }> = {
  feature: { icon: Sparkles, label: 'Новое', tone: 'bg-orange/10 text-orange' },
  improvement: { icon: Wrench, label: 'Улучшение', tone: 'bg-ink/5 text-ink/70' },
  fix: { icon: Bug, label: 'Исправление', tone: 'bg-ink/5 text-ink/60' },
  launch: { icon: Rocket, label: 'Релиз', tone: 'bg-ink text-white' },
};

interface Entry {
  type: EntryType;
  title: string;
  body: string;
}

interface Release {
  date: string;
  version: string;
  title: string;
  entries: Entry[];
}

const RELEASES: Release[] = [
  {
    date: '24 апреля 2026',
    version: 'v1.4',
    title: 'Прозрачность и&nbsp;юридическая база',
    entries: [
      { type: 'feature', title: 'Cookie-баннер с&nbsp;тонкой настройкой', body: 'Три категории, отдельные политики, кнопка управления в&nbsp;футере.' },
      { type: 'feature', title: 'Документы под 152-ФЗ', body: 'Обновлены Политика конфиденциальности и&nbsp;Согласие; добавлены страницы Cookies, Возвраты, Безопасность.' },
      { type: 'feature', title: '/contacts и&nbsp;реквизиты', body: 'Отдельная юридическая страница с&nbsp;реквизитами ИП Раджабов&nbsp;Р.&nbsp;Э.' },
      { type: 'improvement', title: 'SEO-инфраструктура', body: 'sitemap.xml, robots.txt, canonical и&nbsp;OpenGraph для каждой страницы.' },
      { type: 'improvement', title: 'manualChunks и&nbsp;preload Inter', body: 'Меньше JS&nbsp;на&nbsp;первую загрузку, шрифт подгружается параллельно.' },
    ],
  },
  {
    date: '18 апреля 2026',
    version: 'v1.3',
    title: 'Дизайн-апгрейд лендинга',
    entries: [
      { type: 'feature', title: 'Hero и&nbsp;принципы', body: 'Новая композиция: трёхэкранная демонстрация, прозрачные принципы вместо фейковых метрик.' },
      { type: 'feature', title: 'Сравнение с&nbsp;iiko, r_keeper, Poster', body: 'Отдельный блок сравнения по&nbsp;ключевым параметрам.' },
      { type: 'feature', title: 'Калькулятор сети', body: 'Считает стоимость для нескольких заведений на&nbsp;тарифе Network.' },
      { type: 'improvement', title: 'WebP&nbsp;и&nbsp;self-host Inter', body: 'Скриншоты переведены в&nbsp;WebP, шрифт раздаётся с&nbsp;нашего домена.' },
      { type: 'improvement', title: 'Полноэкранное мобильное меню', body: 'Чёткое перекрытие, без полупрозрачных слоёв.' },
    ],
  },
  {
    date: '10 апреля 2026',
    version: 'v1.2',
    title: 'Поддержка и&nbsp;тарифы',
    entries: [
      { type: 'feature', title: 'Раздел поддержки', body: 'WhatsApp, email, режим работы, популярные темы и&nbsp;FAQ.' },
      { type: 'feature', title: 'Тариф Resstro&nbsp;Network', body: 'Сетевой тариф с&nbsp;консолидированной отчётностью.' },
      { type: 'fix', title: 'Корректные хэши JS-бандлов', body: 'Исправлена проблема, при которой клиенты получали кэшированный старый JS&nbsp;после деплоя.' },
    ],
  },
  {
    date: '1 апреля 2026',
    version: 'v1.0',
    title: 'Запуск платформы Resstro',
    entries: [
      { type: 'launch', title: 'Публичный запуск', body: 'Касса, админ-панель и&nbsp;электронное меню в&nbsp;одном продукте, серверы в&nbsp;РФ.' },
      { type: 'feature', title: 'Регистрация и&nbsp;3-дневный пробный период', body: 'Без карты, с&nbsp;полным доступом ко&nbsp;всем функциям.' },
    ],
  },
];

export default function Changelog() {
  return (
    <div className="min-h-screen bg-paper">
      <Header />

      <main className="section">
        <div className="container-site">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[13px] font-medium text-ink/60 transition hover:text-ink"
          >
            <ArrowLeft size={14} strokeWidth={1.75} />
            На главную
          </Link>

          <div className="mt-10 max-w-3xl">
            <span className="eyebrow">Changelog</span>
            <h1 className="h2 mt-4">Что нового в&nbsp;Resstro</h1>
            <p className="mt-6 text-[15px] leading-relaxed text-ink/70">
              Каждый релиз попадает сюда. Если хотите, чтобы какая-то фича появилась раньше,
              напишите в&nbsp;<Link to="/support" className="link">поддержку</Link>{' '}
              или загляните в&nbsp;<Link to="/roadmap" className="link">Roadmap</Link>.
            </p>
          </div>

          <div className="mt-12 max-w-3xl border-t border-line">
            {RELEASES.map((release) => (
              <article key={release.version} className="border-b border-line py-10">
                <header className="flex flex-wrap items-baseline justify-between gap-3">
                  <div className="flex flex-wrap items-baseline gap-4">
                    <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-ink/50">
                      {release.date}
                    </span>
                    <span className="border border-line bg-white px-2.5 py-1 text-[11px] font-medium tabular-nums text-ink">
                      {release.version}
                    </span>
                  </div>
                </header>
                <h2
                  className="mt-3 text-[24px] font-medium leading-snug tracking-tight text-ink"
                  dangerouslySetInnerHTML={{ __html: release.title }}
                />
                <ul className="mt-6 space-y-4">
                  {release.entries.map((e, i) => {
                    const meta = ICONS[e.type];
                    const Icon = meta.icon;
                    return (
                      <li key={i} className="flex items-start gap-4">
                        <span
                          className={
                            'inline-flex items-center gap-1.5 px-2 py-1 text-[10.5px] font-medium uppercase tracking-[0.1em] ' +
                            meta.tone
                          }
                        >
                          <Icon size={11} strokeWidth={1.75} />
                          {meta.label}
                        </span>
                        <div>
                          <div
                            className="text-[14.5px] font-medium text-ink"
                            dangerouslySetInnerHTML={{ __html: e.title }}
                          />
                          <div
                            className="mt-1 text-[13px] leading-relaxed text-ink/65"
                            dangerouslySetInnerHTML={{ __html: e.body }}
                          />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </article>
            ))}
          </div>

          <div className="mt-12 flex flex-wrap gap-3">
            <Link to="/roadmap" className="btn-outline">
              Посмотреть Roadmap
            </Link>
            <Link to="/support" className="btn-ghost">
              Запросить фичу
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
